import React, { useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import rehypeRaw from 'rehype-raw';
import { useTrackLinkClick } from '../hooks/useTrackLinkClick';
import 'highlight.js/styles/github.css';

const REHYPE_RAW_PASS_THROUGH = ['element'];

interface MarkdownMessageProps {
  content: string;
  className?: string;
  messageId?: any;
}

/**
 * Enhanced markdown renderer for SpeedGauge Copilot chat messages
 * 
 * Features:
 * - GitHub Flavored Markdown (tables, strikethrough, task lists)
 * - Syntax highlighting for code blocks
 * - Fleet safety specific styling
 * - Responsive design for mobile devices
 * - Link click tracking (automatically integrated)
 */
export const MarkdownMessage: React.FC<MarkdownMessageProps> = ({ 
  content, 
  className = '',
  messageId = undefined
}) => {
  // Ensure messageId is a number, converting if necessary
  const numericMessageId = messageId !== undefined && typeof messageId !== 'number' 
    ? parseInt(String(messageId), 10)
    : messageId;
  
  // Get link tracking handler - manages auth state internally
  const trackLinkClick = useTrackLinkClick(numericMessageId);
  return (
    <div className={`markdown-message ${className}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[
          rehypeHighlight,
            [rehypeRaw, { passThrough: REHYPE_RAW_PASS_THROUGH }]
        ]}
        components={{
          // Custom code block styling with language detection
          code({ inline, className, children, ...props }: any) {
            const match = /language-(\w+)/.exec(className || '');
            return !inline && match ? (
              <div className="code-block">
                <div className="code-header">
                  <span className="language-label">{match[1].toUpperCase()}</span>
                </div>
                <pre className={`hljs ${className}`}>
                  <code>{children}</code>
                </pre>
              </div>
            ) : (
              <code className="inline-code" {...props}>
                {children}
              </code>
            );
          },
          
          // Enhanced table styling for fleet data
          table({ children }: any) {
            return (
              <div className="table-container">
                <table className="fleet-table">{children}</table>
              </div>
            );
          },
          
          // Safety alerts and important information
          blockquote({ children }: any) {
            return <div className="safety-alert">{children}</div>;
          },
          
          // Custom list styling for safety metrics
          ul({ children, className }: any) {
            const isTaskList = className?.includes('contains-task-list');
            return (
              <ul className={`${isTaskList ? 'task-list' : 'metric-list'} ${className || ''}`}>
                {children}
              </ul>
            );
          },
          
          // Custom ordered list styling
          ol({ children, className }: any) {
            return (
              <ol className={`metric-list ${className || ''}`}>
                {children}
              </ol>
            );
          },
          
          // CRITICAL FIX: Custom list item handling to remove paragraph wrapping
          li({ children, className }: any) {
            // Process all children to unwrap paragraphs
            const processedChildren = React.Children.map(children, (child: any) => {
              // Skip whitespace
              if (typeof child === 'string' && child.trim() === '') {
                return null;
              }
              
              // Check if it's our paragraph component by key pattern
              if (React.isValidElement(child) && (child as any).key?.includes('p-')) {
                return (child as any).props.children;
              }
              
              // Keep other elements (like nested ul/ol) as-is
              return child;
            });
            
            // Filter out null values
            const filteredChildren = processedChildren?.filter((child: any) => child !== null);
            
            return (
              <li className={`metric-list-item ${className || ''}`}>
                {filteredChildren}
              </li>
            );
          },
          
          // Enhanced links with click tracking
          a({ href, children }: any) {
            const isExternal = href?.startsWith('http');
            const isValidUrl = href && (href.startsWith('http') || href.startsWith('/') || href.startsWith('#'));
            
            if (!isValidUrl) {
              return <>{children}</>;
            }

            // Extract link text from children (usually a string for simple links)
            const linkText = typeof children === 'string' 
              ? children 
              : Array.isArray(children) 
                ? children.map((c: any) => typeof c === 'string' ? c : c?.props?.children || '').join('')
                : '';
            
            return (
              <a 
                href={href} 
                target={isExternal ? '_blank' : '_self'}
                rel={isExternal ? 'noopener noreferrer' : undefined}
                className="markdown-link"
                onClick={(e) => {
                  // Track the click (non-blocking)
                  trackLinkClick(href, linkText);
                }}
              >
                {children}
              </a>
            );
          },
          
          // Custom paragraph spacing
          p({ children }: any) {
            return <p className="markdown-paragraph">{children}</p>;
          },
          
          // Enhanced headers with better spacing
          h1({ children }: any) {
            return <h1 className="markdown-h1">{children}</h1>;
          },
          h2({ children }: any) {
            return <h2 className="markdown-h2">{children}</h2>;
          },
          h3({ children }: any) {
            return <h3 className="markdown-h3">{children}</h3>;
          },
          h4({ children }: any) {
            return <h4 className="markdown-h4">{children}</h4>;
          }
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
};

export default MarkdownMessage;
